// Keep locale identity separate from provider availability. A regional request
// may use a generic language record, but never another regional variant.
export function normalizeLocaleCode(value) {
  const raw = typeof value === 'string' ? value : value?.code;
  if (typeof raw !== 'string') return '';
  const parts = raw.trim().replace(/_/g, '-').split('-');
  if (!/^[A-Za-z]{2,8}$/.test(parts[0])
    || parts.slice(1).some(part => !/^[A-Za-z0-9]{1,8}$/.test(part))) return '';
  return parts.map((part, index) => {
    if (!index) return part.toLowerCase();
    if (/^[A-Za-z]{4}$/.test(part)) return part[0].toUpperCase() + part.slice(1).toLowerCase();
    if (/^[A-Za-z]{2}$/.test(part) || /^\d{3}$/.test(part)) return part.toUpperCase();
    return part.toLowerCase();
  }).join('-');
}

function matchingCode(codes, requested) {
  const normalized = normalizeLocaleCode(requested);
  if (!normalized) return undefined;
  const exact = codes.find(code => normalizeLocaleCode(code) === normalized);
  if (exact !== undefined) return exact;
  const base = normalized.split('-')[0];
  return base !== normalized ? codes.find(code => normalizeLocaleCode(code) === base) : undefined;
}

export function resolveLocaleEntry(entries, requested) {
  const available = Array.isArray(entries) ? entries.filter(entry => entry && normalizeLocaleCode(entry.code)) : [];
  const code = matchingCode(available.map(entry => entry.code), requested);
  return code === undefined ? undefined : available.find(entry => entry.code === code);
}

export function resolveLocaleKey(object, requested) {
  if (!object || typeof object !== 'object' || Array.isArray(object)) return undefined;
  return matchingCode(Object.keys(object), requested);
}

// Missing metadata is unknown. Callers decide whether enough positive evidence
// exists; this helper only compares two known locale identities.
export function localesCompatible(first, second) {
  const a = normalizeLocaleCode(first);
  const b = normalizeLocaleCode(second);
  if (!a || !b) return false;
  return a === b || (a.split('-')[0] === b.split('-')[0] && (!a.includes('-') || !b.includes('-')));
}
